import { Injectable } from '@angular/core';
import { deleteField, DocumentReference, getDocs, query, updateDoc, where } from 'firebase/firestore';
import {
  BOIType, IDocumentType,
  IEntity,
  IEntityBoirStatusUpdate, IEntityDocument,
  IEntitySubmitConsent,
  IOwner,
  isHandedOffEntity,
  IShareType,
  IShareTypeReference,
  UUID
} from 'src/app/core/interfaces';
import { EntityService, IEntityOperation, IEntityService } from '../../core/services/entity.service';
import { distinctUntilChanged, Observable, switchMap } from 'rxjs';
import { exhaustiveCheck } from '@cloud-functions/utils';
import {
  IShareTypeChanges
} from "../../modules/entities/components/cap-table/components/configure-share-types-dialog/configure-share-types-dialog.component";
import { UpdateData } from "@firebase/firestore";
import { IEntityCf }  from "@cloud-functions/interfaces";
import { FieldValue } from "firebase-admin/firestore";
import {
  IShareRecordChanges
} from "../../modules/entities/components/cap-table/components/configure-share-records-dialog/configure-share-records-dialog.component";

type IFileEntityOperation = keyof Pick<ISecureFileService,
  | "addOwnerToEntity"
  | "removeOwnerFromEntity"
  | "updateShareTypes"
  | "manageCapTableRecords"
> | IEntityOperation;

type IFileEntityOperationFunction<T extends IFileEntityOperation> = (
  ...args: Parameters<ISecureFileService[T]>
) => Promise<Awaited<ReturnType<ISecureFileService[T]>>>;

interface ISecureFileService extends IEntityService {
  getEntities(userId: UUID): Promise<IEntity[]>;
  // owner-related methods
  getEntityOwners(entityId: UUID): Promise<IOwner[]>;
  addOwnerToEntity(entityId: UUID, ownerId: UUID, boiType: BOIType): Promise<IEntity>;
  removeOwnerFromEntity(entityId: UUID, ownerId: UUID, boiType: BOIType): Promise<IEntity>;
  // fincen-related methods
  submitToFinCEN(entityId: UUID, consentData: IEntitySubmitConsent): Promise<IEntity>;
  updateEntityBoirStatus(entityId: UUID, entityBoirStatusUpdate: IEntityBoirStatusUpdate): Promise<void>
  // capitalization table related methods
  updateShareTypes(entityId: UUID, shareTypeChanges: IShareTypeChanges): Promise<IEntity>
  manageCapTableRecords(entityId: UUID, shareTypeId: UUID, shareRecordChanges: IShareRecordChanges): Promise<IEntity>
  // method curring for entity operations to handle entity mirroring
  createEntityOperation<T extends IFileEntityOperation>(operation: T): IFileEntityOperationFunction<T>;
}

@Injectable({
  providedIn: 'root'
})
export class SecureFileService extends EntityService implements ISecureFileService {
  constructor() {
    super("secure-file");
  }

  public createEntityOperation<T extends IFileEntityOperation>(operation: T) {
    return async (...args: Parameters<ISecureFileService[T]>): Promise<Awaited<ReturnType<ISecureFileService[T]>>> => {
      try {
        const method = this[operation] as IFileEntityOperationFunction<T>;
        const result = await method.apply(this, args);
        try {
          await this.interceptor(operation, args, result);
        } catch (error: unknown) {
          console.error("Error in entity operation interceptor", operation, error);
          throw error;
        }
        return result;
      } catch (error: unknown) {
        console.error("Error in entity operation", operation, error);
        throw error;
      }
    }
  }

  private async interceptor(
    operation: IFileEntityOperation,
    args: Parameters<ISecureFileService[typeof operation]>,
    result: Awaited<ReturnType<ISecureFileService[typeof operation]>>
  ): Promise<void> {
    switch (operation) {
      case "createEntity":
      case "updateEntity":
      case "removeOwnerFromEntity":
      case "addOwnerToEntity":
      case "updateShareTypes":
      case "manageCapTableRecords": {
        const entity = result as Awaited<ReturnType<ISecureFileService[typeof operation]>>;
        const mirrorPromises: Promise<void>[] = [];
        if (isHandedOffEntity(entity) && operation === "updateEntity") {
          mirrorPromises.push(
            this.groupService.mirrorEntityToGroup(entity.id, entity.legalName, entity.managingOrgId, entity.groupId)
          );
        }
        if (isHandedOffEntity(entity)) {
          mirrorPromises.push(
            this.mirrorEntityFromUser(entity.id)
          );
        }
        await Promise.all(mirrorPromises);
        break;
      }
      case "deleteEntity": {
        const entity = result as Awaited<ReturnType<ISecureFileService[typeof operation]>>;
        await this.entityDetailsService.deleteEntityDetails(entity.id);

        if (isHandedOffEntity(entity)) {
          await this.mirrorDeleteEntityFromUser(entity.id, entity.managingOrgId);
        }
        break;
      }
      case "getTotalEntityProgress": {
        const [entityId] = args as Parameters<ISecureFileService[typeof operation]>;
        const totalProgress = result as Awaited<ReturnType<ISecureFileService[typeof operation]>>;
        const entityRef = this.getFileEntityRef(entityId);

        await updateDoc(entityRef, {
          progress: totalProgress.totalPercentFilled
        });

        const entity = await this.getFileEntity(entityId);
        if (isHandedOffEntity(entity)) {
          return this.mirrorEntityProgressFromUser(entity.id, entity.managingOrgId);
        }
        break;
      }
      default:
        exhaustiveCheck(operation, "Invalid operation");
    }
  }

  public async getEntities(userId: UUID): Promise<IEntity[]> {
    const entitiesCollectionDoc = this.getFileEntityCollectionDoc();
    const entityQuery = query(
      entitiesCollectionDoc,
      where("userIds", "array-contains", userId)
    );
    const entitiesSnap = await getDocs(entityQuery);
    const entities = entitiesSnap.docs
      .map((doc) => this.convertEntityFromCf(doc.data()))
    return entities;
  }

  public async getEntityOwners(entityId: UUID): Promise<IOwner[]> {
    const entityData = await this.getFileEntity(entityId);
    const ownerPromises = Object.keys(entityData.ownerRelationships).map(ownerId => {
      return this.ownerService.getById(ownerId);
    });

    return Promise.all(ownerPromises);
  }

  public async addOwnerToEntity(
    entityId: UUID,
    ownerId: UUID,
    boiType: BOIType
  ): Promise<IEntity> {
    const entityRef = this.getFileEntityRef(entityId);
    const entityData = await this.getFileEntity(entityId);
    if (SecureFileService.isOwnerPresentInEntity(entityData, ownerId)) {
      await this.addOwnerBoiType(entityRef, ownerId, boiType);
    } else {
      await this.createOwnerRelationship(entityRef, ownerId, boiType);
    }
    return this.getFileEntity(entityId);
  }

  public async removeOwnerFromEntity(
    entityId: UUID,
    ownerId: UUID,
    boiType: BOIType
  ): Promise<IEntity> {
    const entityRef = this.getFileEntityRef(entityId);
    const entityData = await this.getFileEntity(entityId);

    if (!SecureFileService.isOwnerPresentInEntity(entityData, ownerId)) {
      throw new Error("Owner not found in entity");
    }

    if (Object.keys(entityData.ownerRelationships[ownerId]).length === 2 && boiType) {
      await this.removeOwnerBoiType(entityRef, ownerId, boiType);
    } else {
      await this.removeOwnerRelationship(entityRef, ownerId);
    }

    return this.getFileEntity(entityId);
  }

  public async submitToFinCEN(
    entityId: UUID,
    consentData: IEntitySubmitConsent
  ): Promise<IEntity> {
    const entityData = await this.getFileEntity(entityId);

    const shouldUseBot = await this.featureFlags.shouldUseBot();

    const enqueueResponse = shouldUseBot
      ? await this.boirFilingsService.enqueueWebBOIRFiling(entityId)
      : await this.boirFilingsService.enqueueBOIR(entityId, null, consentData);

    if (!enqueueResponse) {
      throw new Error("Error during submitting to FinCEN");
    }

    const updateEntityOperation = this.createEntityOperation("updateEntity");
    await updateEntityOperation({
      ...entityData,
      status: "e-Filing Pending",
      ...(shouldUseBot ? { consentData } : {}),
    });

    return this.getFileEntity(entityId);
  }

  public async updateEntityBoirStatus(
    entityId: UUID,
    entityBoirStatusUpdate: IEntityBoirStatusUpdate
  ): Promise<void> {
    const entityData = await this.getFileEntity(entityId);

    const updateEntityOperation = this.createEntityOperation("updateEntity");
    await updateEntityOperation({
      ...entityData,
      ...entityBoirStatusUpdate,
    });
  }

  public async updateShareTypes(
    entityId: UUID,
    shareTypeChanges: IShareTypeChanges
  ): Promise<IEntity> {
    const entity = await this.getFileEntity(entityId);

    const deletedShareTypes = await this.handleShareTypesDeletion(
      entity,
      Object.keys(shareTypeChanges.deleted) as UUID[],
    );

    const shareTypeWithChangedUnitsIds = Object.values(shareTypeChanges.updated)
      .filter((updatedShareType): updatedShareType is IShareType => {
        const shareType = entity.capTable?.shareTypes?.[updatedShareType?.id as string];

        if (!updatedShareType || !shareType) return false;

        return shareType.units !== updatedShareType.units;
      })
      .map(shareType => shareType.id);

    await this.handleOwnershipClearing(
      entity,
      shareTypeWithChangedUnitsIds,
    )

    const updatedShareTypes: IShareTypeReference = {
      ...deletedShareTypes,
      ...shareTypeChanges.updated,
      ...shareTypeChanges.created,
    };

    const updateEntity = this.createEntityOperation("updateEntity");

    await updateEntity({
      ...entity,
      capTable: {
        shareTypes: updatedShareTypes,
      },
    });

    return this.getFileEntity(entityId);
  }

  public async manageCapTableRecords(
    entityId: UUID,
    shareTypeId: UUID,
    shareRecordChanges: IShareRecordChanges,
  ): Promise<IEntity> {
    const entityRef = this.getFileEntityRef(entityId);
    const entity = await this.getFileEntity(entityId);

    if (!entity.capTable.shareTypes[shareTypeId]) {
      throw new Error(`Error adding capitalization table record: shareType with id: ${shareTypeId} not found`);
    }

    await updateDoc(entityRef, {
      ...shareRecordChanges,
    } as UpdateData<IEntityCf>);

    return this.getFileEntity(entityId);
  }

  public async clearOwnershipOfCapTableRecords(
    entityId: UUID,
    records: { ownerId: UUID; shareTypeId: UUID }[],
  ): Promise<void> {
    const entity = await this.getFileEntity(entityId);
    const entityRef = this.getFileEntityRef(entityId);

    const updatePayload: Record<string, 0> = {};

    for (const { ownerId, shareTypeId } of records) {
      const exists = !!entity.ownerRelationships[ownerId]?.capTableDetails[shareTypeId];

      if (!exists) {
        throw new Error(`Error updating cap table record: record [${ownerId} -> ${shareTypeId}] not found`);
      }

      updatePayload[`ownerRelationships.${ownerId}.capTableDetails.${shareTypeId}.ownershipAmount`] = 0;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await updateDoc(entityRef as DocumentReference<any>, updatePayload);
  }

  public async removeCapTableRecords(
    entityId: UUID,
    records: { ownerId: UUID; shareTypeId: UUID }[]
  ): Promise<void> {
    const entity = await this.getFileEntity(entityId);
    const entityRef = this.getFileEntityRef(entityId);

    const updatePayload: Record<string, FieldValue> = {};

    for (const { ownerId, shareTypeId } of records) {
      const exists = !!entity.ownerRelationships[ownerId]?.capTableDetails[shareTypeId];

      if (!exists) {
        throw new Error(`Error removing cap table record: record [${ownerId} -> ${shareTypeId}] not found`);
      }

      updatePayload[`ownerRelationships.${ownerId}.capTableDetails.${shareTypeId}`] = deleteField();
    }

    await updateDoc(entityRef, updatePayload);
  }

  public async addDocumentToType(
    entityId: UUID,
    documentTypeId: UUID,
    documents: IEntityDocument[],
  ): Promise<IEntity> {
    const entity = await this.getFileEntity(entityId);

    await Promise.all(
      documents.map(async (document) => {
        const updatedDocument = {
          ...document,
          relatesToEntities: Array.from(
            new Set([...document.relatesToEntities, entityId])
          ),
        } as IEntityDocument;

        return this.documentManagementService.updateDocument(updatedDocument);
      })
    )

    const updatedDocumentTypes = entity.documentTypes.map(docType => {
      return docType.id === documentTypeId
        ? {
          ...docType,
          documents: [
            ...docType.documents,
            ...documents.map(doc => doc.id),
          ],
        } : docType
    });

    return this.updateDocumentTypes(entityId, updatedDocumentTypes);
  }

  public async moveDocumentBetweenTypes(
    entityId: UUID,
    fromDocumentTypeId: UUID,
    toDocumentTypeId: UUID,
    documentId: UUID,
  ): Promise<IEntity> {
    const entity = await this.getFileEntity(entityId);

    const updatedDocumentTypes = entity.documentTypes.map(docType => {
      switch (docType.id) {
        case fromDocumentTypeId:
          return {
            ...docType,
            documents: docType.documents.filter(docId => docId !== documentId),
          }
        case toDocumentTypeId:
          return {
            ...docType,
            documents: [
              ...docType.documents,
              documentId,
            ],
          }
        default:
          return docType
      }
    });

    return this.updateDocumentTypes(entityId, updatedDocumentTypes);
  }

  public async removeDocumentFromType(
    entityId: UUID,
    documentTypeId: UUID,
    document: IEntityDocument,
  ): Promise<IEntity> {
    const entity = await this.getFileEntity(entityId);

    const updatedDocumentTypes = entity.documentTypes.map(docType => {
      return docType.id === documentTypeId
        ? {
          ...docType,
          documents: docType.documents.filter(docId => docId !== document.id),
        } : docType
    });

    await this.documentManagementService.deleteDocumentFromEntity(document.id, entityId);

    return this.updateDocumentTypes(entityId, updatedDocumentTypes);
  }

  public async updateDocumentTypes(
    entityId: UUID,
    updatedDocTypes: IDocumentType[]
  ): Promise<IEntity> {
    const entityRef = this.getFileEntityRef(entityId);

    await updateDoc(entityRef, {
      documentTypes: updatedDocTypes
    })

    return this.getFileEntity(entityId);
  }

  public getFileEntityFirestoreObservable(
    entity$: Observable<IEntity>,
    entityUpdateFilter$: Observable<boolean>,
  ): Observable<IEntity | null> {
    return entity$.pipe(
      distinctUntilChanged(
        (previousEntity, nextEntity) => previousEntity.id === nextEntity.id
      ),
      switchMap((entity) => {
        const entityRef = this.getFileEntityRef(entity.id);
        return this.getEntityFirestoreObservable(entityRef, entityUpdateFilter$)
      }),
    )
  }
}
